import React, { useState, useEffect } from 'react'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material'
import {
  Palette as PaletteIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material'
import { SelectChangeEvent } from '@mui/material/Select'
import { useThemes, useThemeStats } from '../hooks/useThemes'

interface ThemeFilterProps {
  selectedThemes: string[]
  onThemesChange: (themes: string[]) => void
  disabled?: boolean
}

interface Theme {
  name: string
  count?: number
}

const ThemeFilter: React.FC<ThemeFilterProps> = ({
  selectedThemes,
  onThemesChange,
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  // Debounce search term to avoid immediate filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300) // 300ms delay

    return () => clearTimeout(timer)
  }, [searchTerm])

  const { data: themesData, isLoading: themesLoading, error: themesError } = useThemes()
  const { data: statsData, isLoading: statsLoading, error: statsError } = useThemeStats()

  const themes = React.useMemo(() => {
    if (!themesData || !statsData) return []

    const themeStatsMap = new Map()
    if (statsData.themes) {
      statsData.themes.forEach((stat: any) => {
        themeStatsMap.set(stat.name, stat.total || 0)
      })
    }

    let enrichedThemes = themesData.map((theme: any) => ({
      name: theme.name,
      count: themeStatsMap.get(theme.name) || 0
    }))

    // Apply search filter using debounced search term
    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.toLowerCase()
      enrichedThemes = enrichedThemes.filter((theme: Theme) =>
        theme.name.toLowerCase().includes(term)
      )
    }

    // Sort alphabetically by theme name
    enrichedThemes.sort((a: Theme, b: Theme) => {
      return a.name.localeCompare(b.name)
    })

    return enrichedThemes
  }, [themesData, statsData, debouncedSearchTerm])

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value
    const newThemes = typeof value === 'string' ? value.split(',') : value
    onThemesChange(newThemes)
  }

  const handleRemoveTheme = (themeToRemove: string, event: React.MouseEvent) => {
    event.stopPropagation()
    const newThemes = selectedThemes.filter(theme => theme !== themeToRemove)
    onThemesChange(newThemes)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const handleClearSearch = () => {
    setSearchTerm('')
  }

  const handleOpen = () => {
    setIsOpen(true)
    // Auto-focus on search input when dropdown opens
    setTimeout(() => {
      const searchInput = document.querySelector('#theme-search-input') as HTMLInputElement
      if (searchInput) {
        searchInput.focus()
      }
    }, 100)
  }

  const handleClose = () => {
    setIsOpen(false)
    setSearchTerm('')
  }

  const loading = themesLoading || statsLoading
  const error = themesError || statsError



  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}> {/* Reduced from 200 to 180 */}
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading themes...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ minWidth: 180 }}> {/* Reduced from 200 to 180 */}
        {error instanceof Error ? error.message : 'Failed to load themes'}
      </Alert>
    )
  }

  return (
    <Box sx={{
      minWidth: 180, // Reduced from 200 to 180
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      position: 'relative'
    }}> {/* Dynamic width based on content */}
      <FormControl
        fullWidth
        size="small"
        sx={{
          '& .MuiInputBase-root': {
            alignItems: 'baseline'
          }
        }}
      >
        <InputLabel id="theme-filter-label" sx={{ fontSize: '0.8rem' }}>Theme</InputLabel>
        <Select
          labelId="theme-filter-label"
          id="theme-filter"
          multiple
          value={selectedThemes}
          onChange={handleChange}
          open={isOpen}
          onClose={handleClose}
          onOpen={handleOpen}
          label="Theme"
          disabled={disabled}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}> {/* Reduced gap from 0.5 to 0.25 */}
              {selected.map((value) => (
                <Chip
                  key={value}
                  label={value}
                  size="small"
                  onDelete={(event) => handleRemoveTheme(value, event)}
                  onMouseDown={(event) => event.stopPropagation()}
                  sx={{
                    height: 20, // Slightly smaller chip height
                    fontSize: '0.75rem' // Smaller font size
                  }}
                />
              ))}
            </Box>
          )}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 350,
                minWidth: 'auto', // Dynamic width based on content
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
        >
          {/* Search Input */}
          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              id="theme-search-input"
              fullWidth
              size="small"
              placeholder="Search themes..."
              value={searchTerm}
              onChange={handleSearchChange}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                // Allow arrow keys to navigate through menu items
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  return // Don't stop propagation for arrow keys
                }
                e.stopPropagation()
              }}
            />
          </Box>

          {/* Clear All Option */}
          {selectedThemes.length > 0 && (
            <MenuItem
              onClick={() => onThemesChange([])}
              sx={{
                backgroundColor: 'action.hover',
                borderBottom: '1px solid',
                borderColor: 'divider',
                fontWeight: 'bold'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <ClearIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  Clear All ({selectedThemes.length})
                </Typography>
              </Box>
            </MenuItem>
          )}

          {/* Loading indicator */}
          {loading && (
            <MenuItem disabled>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Loading themes...
                </Typography>
              </Box>
            </MenuItem>
          )}

          {/* No results message */}
          {!loading && themes.length === 0 && (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? `No themes found for "${searchTerm}"` : 'No themes available'}
              </Typography>
            </MenuItem>
          )}

          {/* Theme options */}
          {themes.map((theme: Theme) => (
            <MenuItem key={theme.name} value={theme.name}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <PaletteIcon fontSize="small" color="primary" />
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {theme.name}
                </Typography>
                {theme.count !== undefined && (
                  <Chip
                    label={theme.count}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{
                      ml: 1,
                      fontSize: '0.75rem',
                      height: '20px',
                      '& .MuiChip-label': {
                        px: 1
                      }
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )
}

export default ThemeFilter
